import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, BedDouble, CalendarCheck2, FileText, Settings as Cog, LogOut, MessageSquare, Star, Image as ImageIcon, Users, BookOpenText, ShieldCheck, Wallet, Handshake, Menu, X, CalendarRange, Radio, Lock } from "lucide-react";
import { useDynamicLogo } from "@/lib/useLogo";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const ITEMS = [
  { to: "/staff", icon: LayoutDashboard, label: "Dashboard", end: true, perm: null },
  { to: "/staff/channels", icon: Radio, label: "Channel manager", perm: "bookings" },
  { to: "/staff/rooms", icon: BedDouble, label: "Rooms", perm: "rooms" },
  { to: "/staff/pricing", icon: CalendarRange, label: "Pricing calendar", perm: "rooms" },
  { to: "/staff/bookings", icon: CalendarCheck2, label: "Bookings", perm: "bookings" },
  { to: "/staff/payments", icon: Wallet, label: "Payments", perm: "payments" },
  { to: "/staff/reviews", icon: Star, label: "Reviews", perm: "reviews" },
  { to: "/staff/messages", icon: MessageSquare, label: "Messages", perm: "messages" },
  { to: "/staff/media", icon: ImageIcon, label: "Media", perm: "media" },
  { to: "/staff/partners", icon: Handshake, label: "Partners", perm: "partners" },
  { to: "/staff/users", icon: Users, label: "Staff users", perm: "users" },
  { to: "/staff/vault", icon: Lock, label: "Card vault", adminOnly: true },
  { to: "/staff/policies", icon: FileText, label: "Policies", perm: "policies" },
  { to: "/staff/content", icon: BookOpenText, label: "Content blocks", perm: "content_blocks" },
  { to: "/staff/security", icon: ShieldCheck, label: "Security · 2FA", perm: null },
  { to: "/staff/settings", icon: Cog, label: "Site settings", perm: "settings" },
];

export default function StaffLayout({ children }) {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const logoUrl = useDynamicLogo();
  const [perms, setPerms] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      nav("/staff/login", { state: { from: loc.pathname }, replace: true });
    }
  }, [loading, user, nav, loc.pathname]);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/me/permissions").then((r) => setPerms(r.data)).catch(() => {});
  }, [user]);

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Lock body scroll while drawer open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [mobileOpen]);

  if (loading) return <div className="p-12 text-center">Loading…</div>;
  if (!user) {
    return null;
  }
  if (user.role !== "admin" && user.role !== "staff") {
    return (
      <div className="p-12 text-center">
        <h1 className="font-display text-3xl">Staff access only</h1>
        <Link to="/" className="mt-4 inline-block text-brand-primary">← Back to site</Link>
      </div>
    );
  }

  function isAllowed(it) {
    if (it.adminOnly) return user.role === "admin";
    if (!it.perm) return true;
    if (user.role === "admin") return true;
    if (!perms) return true;
    return !!perms.permissions?.[it.perm];
  }

  const currentTitle = ITEMS.find((it) => loc.pathname === it.to || (it.to !== "/staff" && loc.pathname.startsWith(it.to)))?.label || "Staff";

  function Sidebar({ onLinkClick }) {
    return (
      <>
        <Link to="/" className="p-6 border-b border-[#E4E4E7] flex items-center" onClick={onLinkClick}>
          <img src={logoUrl} alt="Berdhill" className="h-14" />
        </Link>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {ITEMS.filter(isAllowed).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={onLinkClick}
              data-testid={`staff-nav-${it.label.toLowerCase().replace(/ /g, "-").replace(/·/g, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-brand-secondary text-brand-primary font-medium" : "text-[#52525B] hover:bg-[#FAFAFA]"
                }`
              }
            >
              <it.icon size={16} /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E4E4E7] text-xs">
          <div className="font-medium text-[#18181B]">{user.name}</div>
          <div className="text-[#71717A] truncate">{user.email}</div>
          <button onClick={logout} data-testid="staff-logout" className="mt-3 flex items-center gap-2 text-[#71717A] hover:text-[#18181B]">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5] text-[#18181B] md:flex">
      {/* Desktop sidebar */}
      <aside className="w-64 bg-white border-r border-[#E4E4E7] hidden md:flex flex-col">
        <Sidebar />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 bg-white border-b border-[#E4E4E7] px-4 py-3 flex items-center gap-3">
        <button
          data-testid="staff-mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 hover:bg-[#FAFAFA] rounded-md"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <img src={logoUrl} alt="Berdhill" className="h-9" />
        <div className="ml-auto text-xs text-[#71717A] truncate max-w-[120px]">{currentTitle}</div>
      </header>

      {/* Mobile slide-out drawer */}
      {mobileOpen && (
        <>
          <div
            data-testid="staff-mobile-overlay"
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            data-testid="staff-mobile-drawer"
            className="md:hidden fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-white z-50 flex flex-col animate-slide-in"
          >
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 hover:bg-[#FAFAFA] rounded-md"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <Sidebar onLinkClick={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 p-4 sm:p-6 md:p-10 overflow-x-hidden min-w-0">{children}</main>

      <style>{`
        @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slideIn 0.22s ease-out; }
      `}</style>
    </div>
  );
}
